/**
 * Tenant detection and management utilities.
 * Supports domain-based and URL parameter-based tenant identification.
 */

/**
 * Extract tenant subdomain from current domain.
 * Examples:
 * - testcompany.mightybot.ai → testcompany
 * - testcompany.localhost → testcompany
 * - app.customdomain.com → null (custom domain, need to lookup)
 */
export function getTenantSubdomain(): string | null {
	const hostname = window.location.hostname;

	// Check for subdomain pattern: subdomain.base-domain
	const parts = hostname.split('.');
	if (parts.length >= 2) {
		// For 2-part domains (e.g., testcompany.localhost): extract first part
		if (parts.length === 2) {
			return parts[0];
		}
		// For 3+ part domains (e.g., testcompany.mightybot.ai): extract first part
		if (parts.length >= 3) {
			return parts[0];
		}
	}

	return null;
}

/**
 * Get tenant_id from URL parameter.
 */
export function getTenantIdFromUrl(): string | null {
	const params = new URLSearchParams(window.location.search);
	return params.get('tenant_id') || params.get('tenant');
}

/**
 * Get tenant subdomain from URL parameter.
 */
export function getTenantSubdomainFromUrl(): string | null {
	const params = new URLSearchParams(window.location.search);
	return params.get('subdomain');
}

/**
 * Store tenant_id in localStorage.
 */
export function setTenantId(tenantId: string): void {
	localStorage.setItem('tenant_id', tenantId);
}

/**
 * Get tenant_id from localStorage.
 */
export function getTenantId(): string | null {
	return localStorage.getItem('tenant_id');
}

/**
 * Clear tenant_id from localStorage.
 */
export function clearTenantId(): void {
	localStorage.removeItem('tenant_id');
}

/**
 * Get tenant identifier (subdomain or tenant_id) from multiple sources.
 * Priority: URL parameter > Domain > localStorage
 */
export function getTenantIdentifier(): {
	tenantId: string | null;
	subdomain: string | null;
} {
	// Try URL parameters first
	const tenantIdFromUrl = getTenantIdFromUrl();
	const subdomainFromUrl = getTenantSubdomainFromUrl();

	if (tenantIdFromUrl) {
		return { tenantId: tenantIdFromUrl, subdomain: null };
	}

	if (subdomainFromUrl) {
		return { tenantId: null, subdomain: subdomainFromUrl };
	}

	// Try domain-based detection
	const subdomainFromDomain = getTenantSubdomain();
	if (subdomainFromDomain) {
		return { tenantId: null, subdomain: subdomainFromDomain };
	}

	// Fallback to localStorage
	const tenantIdFromStorage = getTenantId();
	if (tenantIdFromStorage) {
		return { tenantId: tenantIdFromStorage, subdomain: null };
	}

	return { tenantId: null, subdomain: null };
}
